import { useEffect, useState } from "react";
import { toast } from "sonner";
import { StickyNote, Save } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useSort, SortHead } from "../components/SortHead";
import api, { fmtNum } from "../lib/api";

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthOptions = () => {
  const opts = [];
  const d = new Date();
  for (let i = -6; i < 18; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    opts.push(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
  }
  return opts;
};

const MONTHS_HR = ["siječanj", "veljača", "ožujak", "travanj", "svibanj", "lipanj", "srpanj", "kolovoz", "rujan", "listopad", "studeni", "prosinac"];
const label = (m) => `${MONTHS_HR[parseInt(m.slice(5)) - 1]} ${m.slice(0, 4)}.`;

export default function ReportsPage() {
  const [month, setMonth] = useState(thisMonth());
  const catAccessors = {
    category: (c) => c.category,
    in: (c) => c.inflow,
    out: (c) => c.outflow,
    count: (c) => c.count,
  };
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const { sorted: sortedCats, sort, toggle } = useSort(data?.by_category || [], catAccessors);

  useEffect(() => {
    api.get("/reports", { params: { month } }).then((r) => setData(r.data));
    api.get("/month-notes", { params: { month } }).then((r) => {
      setNote(r.data.text || "");
      setSavedNote(r.data.text || "");
    });
  }, [month]);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.put(`/month-notes/${month}`, { text: note });
      setSavedNote(note.trim());
      toast.success(note.trim() ? "Bilješka spremljena" : "Bilješka obrisana");
    } catch {
      toast.error("Greška pri spremanju bilješke");
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="reports-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Analiza</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Izvještaji</h1>
        </div>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger data-testid="report-month-select" className="w-48 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {monthOptions().map((m) => <SelectItem key={m} value={m}>{label(m)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="report-inflow-card">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-2">Priljev (naplata)</p>
              <p className="font-heading text-2xl font-bold text-[#0D9488] tabular-nums">+{fmtNum(data.inflow_eur, "€")}</p>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="report-outflow-card">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-2">Odljev (plaćanja)</p>
              <p className="font-heading text-2xl font-bold text-[#DC2626] tabular-nums">{fmtNum(data.outflow_eur, "€")}</p>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="report-net-card">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-2">Neto</p>
              <p className={`font-heading text-2xl font-bold tabular-nums ${data.net_eur >= 0 ? "text-[#0D9488]" : "text-[#DC2626]"}`}>{fmtNum(data.net_eur, "€")}</p>
            </div>
          </div>

          <div className="bg-[#F8FAFC] border border-[#D97706]/30 rounded-sm p-5" data-testid="month-note-card">
            <div className="flex items-center gap-2 mb-3">
              <StickyNote className="w-4 h-4 text-[#D97706]" />
              <h2 className="font-heading text-lg font-semibold text-[#0F172A]">Bilješka za {label(month)}</h2>
            </div>
            <Textarea
              data-testid="month-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="npr. veliki odljev zbog nabave opreme; kupac X kasnio s uplatom..."
              maxLength={1000}
              className="bg-white min-h-[70px]"
            />
            {note.trim() !== savedNote && (
              <Button data-testid="save-month-note-btn" size="sm" onClick={saveNote} disabled={savingNote} className="mt-3 bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
                <Save className="w-4 h-4 mr-2" /> {savingNote ? "Spremanje..." : "Spremi bilješku"}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
              <h2 className="font-heading text-lg font-semibold text-[#0F172A] mb-4">Po kategorijama (EUR)</h2>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_category.slice(0, 10)} layout="vertical" margin={{ left: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="category" width={110} tick={{ fontSize: 11, fill: "#0F172A" }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v, n) => [fmtNum(v, "€"), n === "inflow" ? "Priljev" : "Odljev"]} cursor={{ fill: "#F1F5F9" }} />
                    <Bar dataKey="outflow" fill="#DC2626" radius={[0, 2, 2, 0]} name="outflow" />
                    <Bar dataKey="inflow" fill="#0D9488" radius={[0, 2, 2, 0]} name="inflow" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
                    <SortHead label="Kategorija" sortKey="category" sort={sort} onToggle={toggle} />
                    <SortHead label="Priljev €" sortKey="in" sort={sort} onToggle={toggle} className="text-right" align="right" />
                    <SortHead label="Odljev €" sortKey="out" sort={sort} onToggle={toggle} className="text-right" align="right" />
                    <SortHead label="Br." sortKey="count" sort={sort} onToggle={toggle} className="text-right" align="right" />
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="report-category-table">
                  {data.by_category.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center text-[#64748B] py-10">Nema podataka za ovaj mjesec.</TableCell></TableRow>
                  )}
                  {sortedCats.map((c) => (
                    <TableRow key={c.category} className="hover:bg-[#F8FAFC] transition-colors duration-200">
                      <TableCell className="text-sm font-medium">{c.category}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-[#0D9488]">{c.inflow ? `+${fmtNum(c.inflow)}` : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-[#DC2626]">{c.outflow ? `−${fmtNum(c.outflow)}` : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-[#64748B]">{c.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
            <h2 className="font-heading text-lg font-semibold text-[#0F172A] mb-4">Po bankama (EUR)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="report-by-bank">
              {data.by_bank.map((b) => (
                <div key={b.bank} className="border border-[#E2E8F0] rounded-sm p-4">
                  <p className="text-sm font-semibold text-[#0F172A] mb-2">{b.bank}</p>
                  <div className="flex justify-between text-sm tabular-nums">
                    <span className="text-[#0D9488]">+{fmtNum(b.inflow)}</span>
                    <span className="text-[#DC2626]">−{fmtNum(b.outflow)}</span>
                  </div>
                </div>
              ))}
              {data.by_bank.length === 0 && <p className="text-sm text-[#64748B]">Nema podataka.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
